import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, UserCheck, Gavel, Search, Plus, Trash2, Download, CheckCircle2 } from 'lucide-react';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const QUICK = [100, 250, 500, 1000, 2500, 5000];
const BASKET_ITEMS = [
  'Tuscany Getaway',
  'Mountain Getaway',
  'Philadelphia Flyers Ticket Package',
  'Custom Made Kitchen Table',
  'Kindle Scribe',
  'Dinner with the CEO, Josh Fields',
  'Various Liquor Basket',
  'Coach Bag & Wallet',
  'Gift Cards Galore',
  'Daily Tote Bag By Dagne Dover',
  'Digital Picture Frame & Camera',
  'Ninja Creami & Baking Supplies',
];

export default function EventLivePage() {
  const { slug: slugParam } = useParams();
  const slug = slugParam || 'night-in-tuscany';
  const [tab, setTab] = useState('checkin');
  const [guests, setGuests] = useState([]);
  const [gSummary, setGSummary] = useState({ total: 0, checked_in: 0, paddles_assigned: 0 });
  const [pledges, setPledges] = useState([]);
  const [pSummary, setPSummary] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [walkin, setWalkin] = useState('');
  // live ask form
  const [paddle, setPaddle] = useState('');
  const [amount, setAmount] = useState('');
  const [item, setItem] = useState('Donation');
  const [resolved, setResolved] = useState(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, p] = await Promise.all([
        api.get(`/events-live/${slug}/guests`),
        api.get(`/events-live/${slug}/pledges`),
      ]);
      setGuests(g.data.guests); setGSummary(g.data.summary);
      setPledges(p.data.pledges); setPSummary(p.data.summary);
    } catch { toast.error('Load failed — check your access'); }
    setLoading(false);
  }, [api, slug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const n = parseInt(paddle, 10);
    if (Number.isNaN(n)) { setResolved(null); return; }
    const g = guests.find(x => x.paddle_number === n);
    setResolved(g ? { name: g.name } : { missing: true });
  }, [paddle, guests]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? guests.filter(g => g.name.toLowerCase().includes(s) || String(g.paddle_number ?? '').includes(s)) : guests;
  }, [guests, q]);

  const updateGuest = async (g, patch) => {
    try {
      const { data } = await api.put(`/events-live/${slug}/guests/${g.id}`, patch);
      setGuests(prev => prev.map(x => x.id === g.id ? data : x));
      setGSummary(s => ({
        ...s,
        checked_in: guests.filter(x => x.id !== g.id && x.checked_in).length + (data.checked_in ? 1 : 0),
        paddles_assigned: guests.filter(x => x.id !== g.id && x.paddle_number !== null).length + (data.paddle_number !== null ? 1 : 0),
      }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); load(); }
  };

  const addWalkin = async () => {
    if (!walkin.trim()) return;
    try {
      await api.post(`/events-live/${slug}/guests`, { name: walkin.trim() });
      setWalkin(''); toast.success('Walk-in added'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteGuest = async (g) => {
    if (!window.confirm(`Remove ${g.name} from the guest list?`)) return;
    try {
      await api.delete(`/events-live/${slug}/guests/${g.id}`);
      setGuests(prev => prev.filter(x => x.id !== g.id));
      toast.success(`${g.name} removed`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const recordPledge = async () => {
    const n = parseInt(paddle, 10); const amt = parseFloat(amount);
    if (Number.isNaN(n) || !amt) { toast.error('Enter a paddle # and amount'); return; }
    setRecording(true);
    try {
      const { data } = await api.post(`/events-live/${slug}/pledges`, { paddle_number: n, amount: amt, item });
      toast.success(`Paddle #${n} — ${data.guest_name}: ${fmt$(amt)} (${item}) recorded!`);
      setPaddle(''); setAmount(''); setItem('Donation'); setResolved(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setRecording(false);
  };

  const deletePledge = async (p) => {
    if (!window.confirm(`Remove ${fmt$(p.amount)} pledge from ${p.guest_name}?`)) return;
    try { await api.delete(`/events-live/${slug}/pledges/${p.id}`); load(); } catch { toast.error('Failed'); }
  };

  const downloadInvoices = async () => {
    try {
      const resp = await api.get(`/events-live/${slug}/invoices`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${slug}_pledge_invoices.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No pledges recorded yet'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="min-h-screen bg-[#F7F4EF] pb-16" data-testid="event-live-page">
      <div className="bg-[#2E1D49] text-white px-4 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200">Night of event · {slug.replace(/-/g, ' ')}</p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setTab('checkin')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === 'checkin' ? 'bg-white text-[#2E1D49]' : 'bg-white/10 text-white'}`}
                    data-testid="tab-checkin">
              <UserCheck className="h-4 w-4" /> Check-In <span className="opacity-60">({gSummary.checked_in}/{gSummary.total})</span>
            </button>
            <button onClick={() => setTab('liveask')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === 'liveask' ? 'bg-white text-[#2E1D49]' : 'bg-white/10 text-white'}`}
                    data-testid="tab-liveask">
              <Gavel className="h-4 w-4" /> Live Ask <span className="opacity-60">({fmt$(pSummary.total)})</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4 space-y-3">
        {tab === 'checkin' && (
          <>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search guest or paddle #…"
                       className="w-full h-10 pl-9 pr-3 rounded-xl border bg-white text-sm" data-testid="checkin-search" />
              </div>
            </div>
            <div className="flex gap-2">
              <input value={walkin} onChange={e => setWalkin(e.target.value)} onKeyDown={e => e.key === 'Enter' && addWalkin()}
                     placeholder="Add walk-in guest name…" className="flex-1 h-10 px-3 rounded-xl border bg-white text-sm" data-testid="walkin-input" />
              <button onClick={addWalkin} className="h-10 px-4 rounded-xl bg-[#6B3FA0] text-white text-sm font-semibold flex items-center gap-1" data-testid="walkin-add-btn">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {visible.map(g => (
                <div key={g.id} className={`bg-white rounded-xl border p-3 flex items-center gap-3 ${g.checked_in ? 'border-emerald-300' : ''}`} data-testid={`guest-row-${g.id}`}>
                  <button onClick={() => updateGuest(g, { checked_in: !g.checked_in })}
                          className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${g.checked_in ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-400'}`}
                          title={g.checked_in ? 'Checked in — tap to undo' : 'Tap to check in'}
                          data-testid={`checkin-toggle-${g.id}`}>
                    <CheckCircle2 className="h-5 w-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#2B1D3D] truncate">{g.name}</div>
                    <div className="text-[11px] text-gray-400">{g.source}{g.checked_in && g.checked_in_at ? ` · in at ${new Date(g.checked_in_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}</div>
                  </div>
                  <input type="number" inputMode="numeric" placeholder="Paddle"
                         defaultValue={g.paddle_number ?? ''}
                         key={`p-${g.id}-${g.paddle_number}`}
                         onBlur={e => {
                           const v = e.target.value.trim();
                           const n = v === '' ? -1 : parseInt(v, 10);
                           if (n !== (g.paddle_number ?? -1)) updateGuest(g, { paddle_number: n });
                         }}
                         className="w-20 h-9 px-2 rounded-lg border text-center text-sm font-bold text-[#6B3FA0]"
                         data-testid={`paddle-input-${g.id}`} />
                  <button onClick={() => deleteGuest(g)}
                          className="h-9 w-9 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-600 hover:bg-red-50 shrink-0"
                          title="Remove guest" data-testid={`guest-delete-${g.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {visible.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No guests match.</p>}
            </div>
          </>
        )}

        {tab === 'liveask' && (
          <>
            <div className="bg-white rounded-2xl border p-4 space-y-3" data-testid="liveask-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Paddle #</label>
                  <input type="number" inputMode="numeric" value={paddle} onChange={e => setPaddle(e.target.value)}
                         className="w-full h-14 px-3 rounded-xl border text-3xl font-bold text-center text-[#6B3FA0]"
                         data-testid="liveask-paddle" autoFocus />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Amount ($)</label>
                  <input type="number" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)}
                         className="w-full h-14 px-3 rounded-xl border text-3xl font-bold text-center"
                         data-testid="liveask-amount" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Item / Basket</label>
                <select value={item} onChange={e => setItem(e.target.value)}
                        className="w-full h-11 px-3 rounded-xl border text-sm font-semibold text-[#2B1D3D] bg-white"
                        data-testid="liveask-item">
                  <option value="Donation">Donation (Live Ask)</option>
                  {BASKET_ITEMS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              {resolved && (
                <div className={`text-sm font-semibold text-center rounded-lg py-1.5 ${resolved.missing ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`} data-testid="liveask-resolved">
                  {resolved.missing ? 'No guest holds this paddle' : `→ ${resolved.name}`}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {QUICK.map(v => (
                  <button key={v} onClick={() => setAmount(String(v))}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border ${String(v) === amount ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#6B3FA0] border-[#6B3FA0]/30'}`}
                          data-testid={`quick-${v}`}>{fmt$(v)}</button>
                ))}
              </div>
              <button onClick={recordPledge} disabled={recording || resolved?.missing}
                      className="w-full h-12 rounded-xl bg-[#B71C5C] text-white text-lg font-bold disabled:opacity-40 flex items-center justify-center gap-2"
                      data-testid="liveask-record-btn">
                {recording ? <Loader2 className="h-5 w-5 animate-spin" /> : <Gavel className="h-5 w-5" />} Record pledge
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#2B1D3D]" data-testid="liveask-total">
                {pSummary.count} pledges · <span className="text-[#B71C5C]">{fmt$(pSummary.total)}</span> raised
              </div>
              <button onClick={downloadInvoices} className="text-xs text-[#6B3FA0] font-semibold flex items-center gap-1 hover:underline" data-testid="invoices-btn">
                <Download className="h-3.5 w-3.5" /> Invoices (PDF)
              </button>
            </div>
            <div className="space-y-1.5">
              {pledges.map(p => (
                <div key={p.id} className="bg-white rounded-xl border px-3 py-2 flex items-center gap-3 text-sm" data-testid={`pledge-row-${p.id}`}>
                  <span className="h-8 w-8 rounded-full bg-[#EDE7F6] text-[#6B3FA0] font-bold flex items-center justify-center text-xs shrink-0">#{p.paddle_number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.guest_name}</div>
                    {p.item && p.item !== 'Donation' && (
                      <div className="text-[10px] font-semibold text-[#6B3FA0] truncate">🧺 {p.item}</div>
                    )}
                    <div className="text-[10px] text-gray-400">{new Date(p.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })} · by {p.recorded_by}</div>
                  </div>
                  <span className="font-bold text-[#B71C5C]">{fmt$(p.amount)}</span>
                  <button onClick={() => deletePledge(p)} className="text-gray-300 hover:text-red-500" data-testid={`pledge-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {pledges.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No pledges yet — the big moment awaits!</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
